import React, { useEffect, useRef } from 'react'
import {
  ScrollView,
  SizableText,
  Stack,
  XStack,
  YStack,
  isWeb,
  useMedia,
} from '@mythical/ui'
import ContentEpisodeListItem, {
  ContentEpisodeListItemProps,
} from './ContentEpisodesListItem'
import Spinner from 'app/components/primitive/Spinner'
import { Select } from 'app/components/primitive/Form'

// this isn't being replaced properly
import { FlatList } from 'react-native-web'

export interface ContentEpisodesListProps {
  title: string
  currentContentId?: string // Currently watched content id
  loading?: boolean
  episodes: ContentEpisodeListItemProps[]
  seasons?: {
    label: string
    value: string
  }[]
  season?: string
  onSeasonChange?: (season: string) => void
  onContentClick?: (contentId: string) => void
}

export const ContentEpisodesList = (props: ContentEpisodesListProps) => {
  const {
    title,
    currentContentId,
    loading,
    episodes,
    seasons,
    season,
    onSeasonChange,
  } = props
  const media = useMedia()

  const handleSeasonChange = (season: string) => {
    onSeasonChange?.(season)
  }

  const initialScrollIndex = episodes.findIndex(
    (item) => item.id === currentContentId
  )
  
  // Create ref to access the FlatList directly
  const listRef = useRef<FlatList<ContentEpisodeListItemProps>>(null)
  
  useEffect(() => {
    if (loading) return

    if (initialScrollIndex > -1 && listRef.current) {
      // Use setTimeout to ensure the FlatList is rendered before scrolling
      setTimeout(() => {
        try {
          // Use the FlatList's scrollToIndex method directly
          listRef.current?.scrollToIndex({
            index: initialScrollIndex,
            animated: true,
            viewPosition: 0.5
          })
        } catch (error) {
          console.warn('Failed to scroll to index:', error)
        }
      }, 100)
    }
  }, [initialScrollIndex, loading])

  return (
    <YStack
      //maxHeight={600}
      bg="$secondary9"
      br="$3"
      p="$3.5"
      w="100%"
      jc="space-between"
      ai="flex-start"
      gap="$3.5"
      $gtLg={{
        maxWidth: 270,
        maxHeight: 600,
        height: '100%',
      }}
    >
      <YStack
        gap="$3"
        w="100%"
        fd="row"
        jc="space-between"
        flexWrap="wrap"
        $gtMd={{ gap: '$3.5' }}
        $gtLg={{ fd: 'column', jc: 'flex-start' }}
      >
        <SizableText
          ff="$heading"
          tag="h2"
          size="$7"
          fontWeight="500"
          color="$white"
          textTransform="uppercase"
        >
          {title}
        </SizableText>
        {seasons && seasons?.length > 1 && (
          <Select
            id="seasons"
            key={`${seasons?.length}-${season}`}
            hideLabel
            theme="secondary8Alt"
            name="seasons"
            label="Select season"
            value={season}
            onValueChange={handleSeasonChange}
            options={seasons}
            triggerProps={{
              m: 0,
              py: '$1',
              maxWidth: 250,
              $xs: {
                size: '$3',
              },
            }}
          />
        )}
      </YStack>

      <Stack
        w="100%"
        flexGrow={1}
        flexBasis={0}
        position="relative"
        minHeight={188}
        $gtLg={{ w: '100%', maxHeight: '100%' }}
        id="episodes-list"
        overflow="hidden"
      >
        {loading && (
          <XStack
            w="100%"
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={1000}
            jc="center"
            ai="center"
          >
            <Spinner size="large" />
          </XStack>
        )}

        <FlatList
          ref={listRef}
          data={!loading ? episodes : []}
          key={`${episodes?.length}-${season}`}
          renderItem={({ item }) => (
            <Stack
              flex={1}
              $gtLg={{
                mb: '$2.5',
                mr: 0,
              }}
              mr="$2.5"
            >
              <ContentEpisodeListItem
                {...item}
                isActive={currentContentId === item.id}
                onNavigate={(e) => {
                  e.preventDefault()
                  props.onContentClick?.(item.id)
                }}
              />
            </Stack>
          )}
          keyExtractor={(item) => item.id}
          horizontal={!media.gtLg}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ 
            flexGrow: 1,
            height: '100%'
          }}
          disableVirtualization
          getItemLayout={(data, index) => {
            if (media.gtLg) {
              return {
                length: 76,
                offset: index * 76,
                index,
              }
            }
            return {
              length: 240,
              offset: index * 240,
              index,
            }
          }}
        />
      </Stack>
    </YStack>
  )
}
